import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, KeyRound, Loader2, Search, ShieldCheck, UserPlus, X } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import UserAvatar from "../../components/UserAvatar";
import Sheet from "../../components/Sheet";

const ROLES = ["worker", "supervisor", "admin"];
const ROLE_ORDER = { admin: 0, supervisor: 1, worker: 2 };

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // tenta o jeito antigo abaixo
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch (e) {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

function daysLeft(iso) {
  if (!iso) return null;
  return Math.ceil((Date.parse(iso) - Date.now()) / 86400000);
}

function Chip({ tone = "muted", children }) {
  const tones = {
    muted: "border-border bg-muted text-muted-foreground",
    accent: "border-primary/40 bg-primary/10 text-primary",
    ok: "border-green-500/40 bg-green-500/10 text-green-500",
    warn: "border-amber-500/40 bg-amber-500/10 text-amber-500",
    bad: "border-red-500/40 bg-red-500/10 text-red-400",
  };
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const [people, setPeople] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [inviteRole, setInviteRole] = useState("worker");
  const [inviteUses, setInviteUses] = useState(5);
  const [inviteDays, setInviteDays] = useState(7);
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState(null);
  const [showAllInvites, setShowAllInvites] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const load = useCallback(async () => {
    try {
      const [u, i] = await Promise.all([api.get("/company/users"), api.get("/company/invites").catch(() => ({ data: [] }))]);
      setPeople(Array.isArray(u.data) ? u.data : []);
      setInvites(Array.isArray(i.data) ? i.data : []);
    } catch (e) {
      toast.error("Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pending = useMemo(() => people.filter((p) => p.status === "pending"), [people]);
  const members = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => p.status !== "pending")
      .filter((p) => roleFilter === "all" || p.role === roleFilter)
      .filter((p) => !q || (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q))
      .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || (a.name || "").localeCompare(b.name || ""));
  }, [people, query, roleFilter]);

  const activeCount = people.filter((p) => p.status === "active").length;
  const adminCount = people.filter((p) => p.role === "admin" && p.status === "active").length;
  const selected = people.find((p) => p.id === selectedId) || null;

  const patchUser = async (person, changes, doneMessage) => {
    setBusyAction("patch");
    try {
      await api.patch(`/company/users/${person.id}`, { status: person.status, role: person.role, ...changes });
      toast.success(doneMessage);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update this user.");
    } finally {
      setBusyAction("");
    }
  };

  const resetPassword = async () => {
    if (!selected) return;
    if (newPassword.length < 6) {
      toast.error("The password needs at least 6 characters.");
      return;
    }
    setBusyAction("password");
    try {
      await api.post(`/company/users/${selected.id}/reset-password`, { new_password: newPassword });
      toast.success("Password updated");
      setNewPassword("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not change the password.");
    } finally {
      setBusyAction("");
    }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/company/invites", { role: inviteRole, uses: Number(inviteUses), days_valid: Number(inviteDays) });
      setFresh(data);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create the invite code.");
    } finally {
      setCreating(false);
    }
  };

  const copyCode = async (code) => {
    const ok = await copyText(code);
    if (ok) toast.success("Code copied");
    else toast.error("Could not copy. Select the code and copy it.");
  };

  const sortedInvites = useMemo(
    () => [...invites].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))),
    [invites]
  );
  const shownInvites = showAllInvites ? sortedInvites : sortedInvites.slice(0, 4);

  const selectClass =
    "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary";

  return (
    <div className="mx-auto max-w-4xl px-4 pb-10 pt-5 md:px-8 md:pt-10">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Users &amp; Access</h1>
        <p className="mt-1 text-sm text-muted-foreground">Approve signups, manage roles and invite people to your company.</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Active", value: activeCount },
          { label: "Pending", value: pending.length, hot: pending.length > 0 },
          { label: "Admins", value: adminCount },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl border bg-card p-4 ${s.hot ? "border-primary/50" : "border-border"}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className={`mt-1 text-3xl font-bold ${s.hot ? "text-primary" : ""}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Aprovacoes pendentes */}
      {pending.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-3 text-lg font-semibold">Waiting for approval</h2>
          <div className="space-y-3">
            {pending.map((p) => (
              <div key={p.id} className="rounded-xl border border-primary/40 bg-card p-4">
                <div className="flex items-center gap-3">
                  <UserAvatar url={p.avatar_url} name={p.name} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{p.name}</div>
                    <div className="truncate text-sm text-muted-foreground">{p.email}</div>
                  </div>
                  <Chip tone="accent">{p.role}</Chip>
                </div>
                {isAdmin && (
                  <div className="mt-3 flex gap-3">
                    <button
                      type="button"
                      onClick={() => patchUser(p, { status: "rejected" }, "Request rejected")}
                      disabled={busyAction === "patch"}
                      className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-border font-semibold disabled:opacity-60"
                    >
                      <X className="h-5 w-5" /> Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => patchUser(p, { status: "active" }, "User approved")}
                      disabled={busyAction === "patch"}
                      className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-primary font-semibold text-black disabled:opacity-60"
                    >
                      <Check className="h-5 w-5" /> Approve
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Convites */}
      {isAdmin && (
        <section className="mt-8">
          <h2 className="mb-3 text-lg font-semibold">Invite code</h2>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor="inv-role" className="mb-1.5 block text-sm font-medium">
                  Role
                </label>
                <select id="inv-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className={`${selectClass} capitalize`}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="inv-uses" className="mb-1.5 block text-sm font-medium">
                  Can be used
                </label>
                <select id="inv-uses" value={inviteUses} onChange={(e) => setInviteUses(e.target.value)} className={selectClass}>
                  {[1, 5, 10, 25].map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? "time" : "times"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="inv-days" className="mb-1.5 block text-sm font-medium">
                  Valid for
                </label>
                <select id="inv-days" value={inviteDays} onChange={(e) => setInviteDays(e.target.value)} className={selectClass}>
                  {[1, 7, 30].map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? "day" : "days"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="button"
              onClick={createInvite}
              disabled={creating}
              className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary font-semibold text-black disabled:opacity-60"
              data-testid="generate-invite"
            >
              {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserPlus className="h-5 w-5" />}
              Generate code
            </button>

            {fresh && (
              <div className="mt-4 rounded-xl border border-primary/50 bg-primary/10 p-4 text-center">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">New code</div>
                <div className="mt-1 font-mono text-4xl font-bold tracking-[0.25em] text-primary">{fresh.code}</div>
                <div className="mt-1 text-xs text-muted-foreground capitalize">
                  {fresh.role} | {fresh.uses_left} {fresh.uses_left === 1 ? "use" : "uses"}
                </div>
                <button
                  type="button"
                  onClick={() => copyCode(fresh.code)}
                  className="mt-3 inline-flex h-11 items-center gap-2 rounded-lg border border-border bg-card px-5 font-semibold"
                >
                  <Copy className="h-4 w-4" /> Copy code
                </button>
              </div>
            )}
          </div>

          {shownInvites.length > 0 && (
            <div className="mt-3 space-y-2">
              {shownInvites.map((inv) => {
                const left = daysLeft(inv.expires_at);
                const expired = left !== null && left <= 0;
                const used = (inv.uses_left ?? 0) <= 0;
                return (
                  <div key={inv.id} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className={`font-mono text-lg font-bold tracking-widest ${expired || used ? "text-muted-foreground line-through" : ""}`}>
                        {inv.code}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="capitalize">{inv.role}</span>
                        <span>{inv.uses_left} left</span>
                        <span>{expired ? "Expired" : left === 1 ? "1 day left" : `${left} days left`}</span>
                      </div>
                    </div>
                    {!expired && !used && (
                      <button
                        type="button"
                        onClick={() => copyCode(inv.code)}
                        aria-label="Copy code"
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-border active:bg-accent"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
              {sortedInvites.length > 4 && (
                <button
                  type="button"
                  onClick={() => setShowAllInvites((v) => !v)}
                  className="h-11 w-full rounded-lg text-sm font-medium text-muted-foreground active:bg-accent"
                >
                  {showAllInvites ? "Show fewer" : `Show all ${sortedInvites.length} codes`}
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {/* Equipe */}
      <section className="mt-8">
        <h2 className="mb-3 text-lg font-semibold">Team</h2>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email"
            className="h-12 w-full rounded-lg border border-input bg-background pl-11 pr-4 text-base outline-none focus:border-primary"
          />
        </div>

        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {["all", ...ROLES].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRoleFilter(r)}
              className={`h-10 shrink-0 rounded-full border px-4 text-sm font-medium capitalize ${
                roleFilter === r ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              {r === "all" ? "Everyone" : r}
            </button>
          ))}
        </div>

        {loading && <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>}

        {!loading && members.length === 0 && (
          <div className="py-12 text-center text-sm text-muted-foreground">No one matches this search.</div>
        )}

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {members.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setSelectedId(p.id);
                setNewPassword("");
              }}
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left active:bg-accent"
              data-testid={`member-${p.id}`}
            >
              <UserAvatar url={p.avatar_url} name={p.name} size={48} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">{p.name}</span>
                  {p.id === me?.id && <Chip tone="muted">You</Chip>}
                </div>
                <div className="truncate text-sm text-muted-foreground">{p.email}</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Chip tone={p.role === "admin" ? "accent" : "muted"}>{p.role}</Chip>
                  {p.status === "rejected" && <Chip tone="bad">Suspended</Chip>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Gerenciar uma pessoa */}
      {selected && (
        <Sheet title={selected.name} subtitle={selected.email} onClose={() => !busyAction && setSelectedId(null)}>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <UserAvatar url={selected.avatar_url} name={selected.name} size={64} />
              <div className="flex flex-wrap gap-2">
                <Chip tone={selected.role === "admin" ? "accent" : "muted"}>{selected.role}</Chip>
                <Chip tone={selected.status === "active" ? "ok" : "bad"}>{selected.status === "active" ? "Active" : "Suspended"}</Chip>
              </div>
            </div>

            {isAdmin ? (
              <>
                <div>
                  <div className="mb-2 text-sm font-medium">Role</div>
                  <div className="grid grid-cols-3 gap-2">
                    {ROLES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        disabled={selected.id === me?.id || busyAction === "patch"}
                        onClick={() => selected.role !== r && patchUser(selected, { role: r }, `Role changed to ${r}`)}
                        className={`h-12 rounded-lg border text-sm font-semibold capitalize disabled:opacity-50 ${
                          selected.role === r ? "border-primary bg-primary/15 text-primary" : "border-border"
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                  {selected.id === me?.id && <p className="mt-2 text-xs text-muted-foreground">You cannot change your own role.</p>}
                </div>

                <div>
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                    <KeyRound className="h-4 w-4" /> Set a new password
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      autoComplete="off"
                      className="h-12 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={resetPassword}
                      disabled={busyAction === "password"}
                      className="flex h-12 shrink-0 items-center justify-center rounded-lg bg-primary px-5 font-semibold text-black disabled:opacity-60"
                    >
                      {busyAction === "password" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
                    </button>
                  </div>
                </div>

                {selected.id !== me?.id && (
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                      <ShieldCheck className="h-4 w-4" /> Access
                    </div>
                    {selected.status === "active" ? (
                      <button
                        type="button"
                        onClick={() => patchUser(selected, { status: "rejected" }, "Access suspended")}
                        disabled={busyAction === "patch"}
                        className="h-12 w-full rounded-lg border border-red-500/50 font-semibold text-red-400 disabled:opacity-60"
                      >
                        Suspend access
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => patchUser(selected, { status: "active" }, "Access restored")}
                        disabled={busyAction === "patch"}
                        className="h-12 w-full rounded-lg bg-primary font-semibold text-black disabled:opacity-60"
                      >
                        Restore access
                      </button>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">A suspended person cannot sign in. Their records are kept.</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Only admins can change roles and access.</p>
            )}
          </div>
        </Sheet>
      )}
    </div>
  );
}
